import React, { useState } from 'react';
import './WikiDrop.css';

function WikiDrop() {
  const [activeSection, setActiveSection] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');

  const wikiSections = {
    overview: {
      title: 'Overview',
      content: (
        <div>
          <h3>What is WikiDrop?</h3>
          <p>
            WikiDrop is the comprehensive knowledge base and information center for the NitroDrop platform. 
            This wiki contains detailed information about the platform, its features, history, community, 
            and everything you need to know about participating in giveaways and using the platform.
          </p>
          <h3>Quick Facts</h3>
          <ul>
            <li><strong>Founded:</strong> 2024</li>
            <li><strong>Platform Type:</strong> Discord Nitro & Cash Giveaway Platform</li>
            <li><strong>Total Users:</strong> 1000+</li>
            <li><strong>Completed Giveaways:</strong> 500+</li>
            <li><strong>Total Prizes Awarded:</strong> $50,000+</li>
          </ul>
        </div>
      ),
    },
    history: {
      title: 'History',
      content: (
        <div>
          <h3>Early Development (2024)</h3>
          <p>
            NitroDrop was conceived in early 2024 by founder 0b.livion with the vision of creating 
            a fair and transparent platform for Discord community members to participate in giveaways. 
            The initial version focused on basic giveaway functionality with local storage.
          </p>
          <h3>Platform Evolution</h3>
          <p>
            Throughout 2024, the platform underwent significant development:
          </p>
          <ul>
            <li><strong>Q1 2024:</strong> Initial launch with basic giveaway features</li>
            <li><strong>Q2 2024:</strong> Introduction of entry boost system and admin panel</li>
            <li><strong>Q3 2024:</strong> Real-time chat and messaging features</li>
            <li><strong>Q4 2024:</strong> Database migration and API implementation</li>
          </ul>
          <h3>Current Status</h3>
          <p>
            As of late 2024, NitroDrop has evolved into a full-featured platform with database backend, 
            real-time features, and comprehensive user management. The platform continues to grow with 
            new features and improvements being added regularly.
          </p>
        </div>
      ),
    },
    features: {
      title: 'Features',
      content: (
        <div>
          <h3>Core Features</h3>
          <div className="feature-grid">
            <div className="feature-item">
              <h4>🎁 Multiple Giveaways</h4>
              <p>Enter multiple active giveaways simultaneously for Discord Nitro, cash, and gift cards.</p>
            </div>
            <div className="feature-item">
              <h4>⚡ Entry Boost System</h4>
              <p>Get 2x or 4x entry multipliers with our boost system to increase your chances of winning.</p>
            </div>
            <div className="feature-item">
              <h4>🎯 Fair Draw System</h4>
              <p>100% fair random selection ensures transparency and fairness for all participants.</p>
            </div>
            <div className="feature-item">
              <h4>💰 Big Prizes</h4>
              <p>Win Discord Nitro subscriptions, PayPal cash, Steam gift cards, and more valuable prizes.</p>
            </div>
            <div className="feature-item">
              <h4>💬 Real-time Chat</h4>
              <p>Community chat with WebSocket support for instant messaging and interaction.</p>
            </div>
            <div className="feature-item">
              <h4>📨 Private Messaging</h4>
              <p>Direct messaging system for private conversations between users.</p>
            </div>
            <div className="feature-item">
              <h4>🎨 Custom Themes</h4>
              <p>Choose from 10+ color themes to personalize your experience.</p>
            </div>
            <div className="feature-item">
              <h4>📢 Announcements</h4>
              <p>Stay updated with important announcements and platform news.</p>
            </div>
            <div className="feature-item">
              <h4>👤 User Profiles</h4>
              <p>Detailed user profiles with entry history and statistics.</p>
            </div>
            <div className="feature-item">
              <h4>🛡️ Admin Panel</h4>
              <p>Comprehensive management tools for administrators.</p>
            </div>
            <div className="feature-item">
              <h4>📊 Statistics</h4>
              <p>Real-time statistics on giveaways, participants, and prizes.</p>
            </div>
            <div className="feature-item">
              <h4>🔒 Secure Authentication</h4>
              <p>JWT-based authentication with secure password hashing.</p>
            </div>
          </div>
        </div>
      ),
    },
    technology: {
      title: 'Technology Stack',
      content: (
        <div>
          <h3>Frontend</h3>
          <ul>
            <li><strong>React.js 18.2.0</strong> - Modern UI framework for building interactive user interfaces</li>
            <li><strong>CSS3</strong> - Advanced styling with CSS variables for theming</li>
            <li><strong>WebSocket API</strong> - Real-time communication for chat features</li>
          </ul>
          <h3>Backend</h3>
          <ul>
            <li><strong>Node.js</strong> - JavaScript runtime environment</li>
            <li><strong>Express.js</strong> - Web application framework</li>
            <li><strong>MongoDB</strong> - NoSQL database for data storage</li>
            <li><strong>Mongoose</strong> - MongoDB object modeling</li>
            <li><strong>WebSocket (express-ws)</strong> - Real-time bidirectional communication</li>
          </ul>
          <h3>Security & Authentication</h3>
          <ul>
            <li><strong>JWT (JSON Web Tokens)</strong> - Secure token-based authentication</li>
            <li><strong>bcryptjs</strong> - Password hashing and security</li>
            <li><strong>CORS</strong> - Cross-origin resource sharing</li>
          </ul>
          <h3>Deployment</h3>
          <ul>
            <li><strong>Vercel/Netlify</strong> - Frontend hosting</li>
            <li><strong>Heroku/Railway</strong> - Backend hosting options</li>
            <li><strong>MongoDB Atlas</strong> - Cloud database hosting</li>
          </ul>
        </div>
      ),
    },
    community: {
      title: 'Community',
      content: (
        <div>
          <h3>Discord Server</h3>
          <p>
            NitroDrop maintains an active Discord community where users can:
          </p>
          <ul>
            <li>Get support and assistance</li>
            <li>Stay updated on new giveaways</li>
            <li>Interact with other community members</li>
            <li>Receive announcements and updates</li>
            <li>Participate in community events</li>
          </ul>
          <a 
            href="https://discord.gg/RXVZZWzMcq" 
            target="_blank" 
            rel="noopener noreferrer"
            className="discord-link-btn"
          >
            Join Discord Server
          </a>
          <h3>Community Guidelines</h3>
          <ul>
            <li>Be respectful to all members</li>
            <li>Follow Discord Terms of Service</li>
            <li>No spam or harassment</li>
            <li>Report issues to moderators</li>
            <li>Help maintain a positive environment</li>
          </ul>
        </div>
      ),
    },
    rules: {
      title: 'Rules & Guidelines',
      content: (
        <div>
          <h3>Giveaway Rules</h3>
          <ol>
            <li><strong>One entry per giveaway per person</strong> – no alt accounts or abuse allowed.</li>
            <li>Use your real Discord username and valid contact information.</li>
            <li>Fake or spam entries will result in instant disqualification.</li>
            <li>Winners must reply within <strong>24 hours</strong> when contacted.</li>
            <li>Being a member of our Discord server may be required to claim prizes.</li>
            <li>Winners are selected via random draw or wheel spin.</li>
            <li>This platform is not affiliated with Discord, Inc. or any related entity.</li>
            <li>Rules may be updated to keep things fair for all participants.</li>
          </ol>
          <h3>Account Rules</h3>
          <ul>
            <li>One account per person</li>
            <li>No sharing accounts</li>
            <li>Keep your account secure</li>
            <li>Report suspicious activity</li>
          </ul>
          <h3>Consequences</h3>
          <p>
            Violation of rules may result in:
          </p>
          <ul>
            <li>Entry disqualification</li>
            <li>Account suspension</li>
            <li>Permanent ban</li>
          </ul>
        </div>
      ),
    },
    faq: {
      title: 'FAQ',
      content: (
        <div>
          <div className="faq-item">
            <h4>Q: How do I enter a giveaway?</h4>
            <p>A: Simply click on any active giveaway, fill out the entry form, and submit. Your entry will be recorded automatically.</p>
          </div>
          <div className="faq-item">
            <h4>Q: How are winners selected?</h4>
            <p>A: Winners are selected randomly using a fair random draw system. Each entry has an equal chance of winning.</p>
          </div>
          <div className="faq-item">
            <h4>Q: What are entry boosts?</h4>
            <p>A: Entry boosts multiply your entries. A 2x boost doubles your entries, and a 4x boost quadruples them.</p>
          </div>
          <div className="faq-item">
            <h4>Q: How do I get extra entries?</h4>
            <p>A: You can purchase entry boosts, participate in special events, or complete certain tasks to earn extra entries.</p>
          </div>
          <div className="faq-item">
            <h4>Q: When will I receive my prize if I win?</h4>
            <p>A: Prizes are typically delivered within 24-48 hours after winner confirmation, depending on the prize type.</p>
          </div>
          <div className="faq-item">
            <h4>Q: Can I enter multiple giveaways?</h4>
            <p>A: Yes! You can enter as many active giveaways as you want simultaneously.</p>
          </div>
          <div className="faq-item">
            <h4>Q: Is this platform affiliated with Discord?</h4>
            <p>A: No, NitroDrop is an independent platform and is not affiliated with Discord, Inc.</p>
          </div>
          <div className="faq-item">
            <h4>Q: How do I contact support?</h4>
            <p>A: Join our Discord server or use the messaging system on the platform to contact administrators.</p>
          </div>
        </div>
      ),
    },
    statistics: {
      title: 'Statistics',
      content: (
        <div>
          <div className="stats-grid-large">
            <div className="stat-box-large">
              <div className="stat-icon">👥</div>
              <div className="stat-number">1000+</div>
              <div className="stat-label">Active Users</div>
            </div>
            <div className="stat-box-large">
              <div className="stat-icon">🎁</div>
              <div className="stat-number">500+</div>
              <div className="stat-label">Completed Giveaways</div>
            </div>
            <div className="stat-box-large">
              <div className="stat-icon">💰</div>
              <div className="stat-number">$50,000+</div>
              <div className="stat-label">Total Prizes Awarded</div>
            </div>
            <div className="stat-box-large">
              <div className="stat-icon">⏰</div>
              <div className="stat-number">24/7</div>
              <div className="stat-label">Support Available</div>
            </div>
            <div className="stat-box-large">
              <div className="stat-icon">🎯</div>
              <div className="stat-number">100%</div>
              <div className="stat-label">Fair & Transparent</div>
            </div>
            <div className="stat-box-large">
              <div className="stat-icon">🚀</div>
              <div className="stat-number">2024</div>
              <div className="stat-label">Year Founded</div>
            </div>
          </div>
        </div>
      ),
    },
  };

  const filteredSections = Object.keys(wikiSections).filter(key => {
    if (!searchQuery) return true;
    const section = wikiSections[key];
    return section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
           section.content.props.children.toString().toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <div className="wikidrop-page">
      <div className="wikidrop-header">
        <h1 className="wikidrop-title">📚 WikiDrop</h1>
        <p className="wikidrop-subtitle">The Complete Knowledge Base for NitroDrop</p>
        <div className="wiki-search">
          <input
            type="text"
            placeholder="Search WikiDrop..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="wiki-search-input"
          />
        </div>
      </div>

      <div className="wikidrop-container">
        <div className="wiki-sidebar">
          <h3>Contents</h3>
          <ul className="wiki-nav">
            {Object.keys(wikiSections).map((key) => (
              <li key={key}>
                <button
                  className={activeSection === key ? 'active' : ''}
                  onClick={() => setActiveSection(key)}
                >
                  {wikiSections[key].title}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="wiki-content">
          <div className="wiki-article">
            <h2>{wikiSections[activeSection].title}</h2>
            <div className="wiki-body">
              {wikiSections[activeSection].content}
            </div>
            <div className="wiki-footer">
              <p>Last updated: {new Date().toLocaleDateString()}</p>
              <div className="wiki-actions">
                <button className="wiki-action-btn">Edit</button>
                <button className="wiki-action-btn">Share</button>
                <button className="wiki-action-btn">Print</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WikiDrop;

